import {all, call, fork, put, race, select, take, takeLatest} from "redux-saga/effects";
import {IApplicationState} from "../index";
import {delay} from "redux-saga";
import {closeAds, loadAds, showAds, watchAds} from "./actions";
import {getType} from "typesafe-actions";
import {trackError} from "../log/actions";
import {LogLevel} from "../log/types";
import {AdType} from "./types";
import {AnyAction} from "redux";
import {endMetric, startMetric} from "../log/performance";
import forkWithErrHandler from "../../shared/utils/forkWithErrHandler";
import { SDK } from "../../sdk";
import { mute, unmute } from "../../shared/sounds";

function* loadAd(adType: AdType) {
    const metricId = yield call(startMetric, "loadAd", adType);

    try {
        const isSuccess = yield call([SDK.instance, SDK.instance.loadAd], adType);
        if (isSuccess) {
            yield put(loadAds(adType));
        }

        return isSuccess;
    }
    catch (error) {
        yield put(trackError({
            logLevel: LogLevel.Warn,
            category: "AdvertisementSaga",
            message: `Failed to load ${adType} ad`,
            error: error
        }));

        return false;
    }
    finally {
        yield fork(endMetric, metricId);
    }
}

function* showAd(adType: AdType) {
    const { payload: { reward } } = yield take((action: AnyAction) => {
        return action.type === getType(watchAds)
            && action.payload.adType === adType;
    });

    let success = false;

    try {
        mute();

        yield put(showAds({ adType, reward}));

        const {showAdTimeout} = yield select(({ settings }: IApplicationState) => settings.advertisement);

        yield race({
            success: call([SDK.instance, SDK.instance.showAd], adType),
            timeout: call(function* () {
                yield delay(showAdTimeout || 30000);
                throw new Error('Timeout exceeded');
            })
        });

        return success = true;
    }
    catch (error) {
        yield put(trackError({
            logLevel: LogLevel.Warn,
            category: "AdvertisementSaga",
            message: `Failed to show ${adType} ad`,
            error: error
        }));

        return false;
    }
    finally {
        unmute();

        yield put(closeAds({
            adType,
            reward,
            success    
        }));
    }
}

function* adsWorker(adType: AdType) {
    const settings = yield select(({ settings }: IApplicationState) => settings.advertisement);
    let attempt = 0;

    if (adType === 'interstitial') {
        yield delay(settings.interstitialAdDelay);
    }

    do {
        try {
            const isLoaded = yield call(loadAd, adType);

            if (isLoaded) {
                const isWatched = yield call(showAd, adType);
                if (isWatched) {
                    attempt = 0;
                }
                else if (++attempt > settings.showAdRetryCount) {
                    break;
                }
            }
        }
        catch (error) {
            yield put(trackError({
                logLevel: LogLevel.Warn,
                category: "AdvertisementSaga",
                message: "adsWorker failed",
                error: error,
                data: {
                    adType
                }
            }));

            if (++attempt > settings.showAdRetryCount) {
                break;
            }
        }

        yield delay(adType === 'rewarded' 
            ? settings.rewardedAdDelay 
            : settings.interstitialAdDelay);
    }
    while (true);
}

function* holdInterstitialAdsAfterRewarded() {
    yield takeLatest((action: AnyAction) => {
        return action.type === getType(showAds)
            && action.payload === 'rewarded';
    }, function* () {
        const {interstitialAfterRewardedDelay, interstitialAdIsAvailable} = yield select(({ settings }: IApplicationState) => settings.advertisement);

        if (interstitialAdIsAvailable) {
            const { payload: { success } } = yield take(getType(closeAds));

            if (success) {
                yield delay(interstitialAfterRewardedDelay);

                yield put(loadAds('interstitial'));
            }
        }
    });
}

export default function* advertisementSaga() {
    const tasks = [];

    if (SDK.instance.supports.rewardedAds) {
        tasks.push(forkWithErrHandler("advertisementSaga", "adsWorker", () => adsWorker('rewarded')));
    }

    if (SDK.instance.supports.interstitialAds) {
        tasks.push(forkWithErrHandler("advertisementSaga", "adsWorker", () => adsWorker('interstitial')));
        tasks.push(forkWithErrHandler("advertisementSaga", "holdInterstitialAdsAfterRewarded", holdInterstitialAdsAfterRewarded));
    }

    if (!tasks.length)
        return;

    yield all(tasks);
}